'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Input, Spinner } from '@common/components';
import { useClipboard } from '@common/hooks';
import { assert, cn } from '@core/utils';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { AuthInfiniteQueryKey, AuthInfiniteQueryOptions, AuthMutation } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PageNavigation } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES } from '@/constants';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { PointMarketListItem } from './_components/PointMarketListItem';
const PAGE_SIZE = 8;

/**
 * 실제 의미
 * ORDERED("POINT_MARKET_ORDER_STATUS_ORDERED", "주문(쿠폰 사용전)"),
  CANCELED("POINT_MARKET_ORDER_STATUS_CANCELED", "주문 취소"),
  COUPON_ISSUING("POINT_MARKET_ORDER_STATUS_COUPON_ISSUING", "쿠폰 발행 중"),
  COUPON_ISSUED("POINT_MARKET_ORDER_STATUS_COUPON_ISSUED", "쿠폰 발행 완료"),
  COUPON_FAILED("POINT_MARKET_ORDER_STATUS_COUPON_FAILED", "쿠폰 발급 실패"),
  COMPLETED("POINT_MARKET_ORDER_STATUS_COMPLETED", "쿠폰 사용"),
  EXPIRED("POINT_MARKET_ORDER_STATUS_EXPIRED", "쿠폰 만료"),
 */
const ORDER_STATUS = {
  CANCELED: '취소',
  COMPLETED: '사용완료',
  COUPON_FAILED: '사용전',
  COUPON_ISSUED: '사용전',
  COUPON_ISSUING: '사용전',
  EXPIRED: '만료',
  ORDERED: '사용전'
};
export default function MyInfoPointMarketPage() {
  const $ = _c(33);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...filledInfiniteQueryOptions(AuthInfiniteQueryOptions["get:/wello/v2/auth/point-market/order"]({
        query: {
          page: 1,
          size: PAGE_SIZE
        }
      })),
      select: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: data_0,
    hasNextPage,
    isLoading,
    isFetching,
    fetchNextPage
  } = useInfiniteQuery(t0);
  let t1;
  if ($[1] !== fetchNextPage || $[2] !== isFetching) {
    t1 = {
      onChange: inView => {
        if (!isFetching && inView) {
          fetchNextPage();
        }
      }
    };
    $[1] = fetchNextPage;
    $[2] = isFetching;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    ref
  } = useInView(t1);
  const [selectedCouponCode, setSelectedCouponCode] = useState("");
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setSelectedCouponCode("");
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const closeBottomDrawer = t2;
  const queryClient = useQueryClient();
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = AuthMutation["post:/wello/v2/auth/point-market/order/reissue-coupon"]();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== queryClient) {
    t4 = {
      ...t3,
      onSuccess: async t5 => {
        const {
          context
        } = t5;
        assert(context?.couponCode);
        setSelectedCouponCode(context.couponCode);
        queryClient.invalidateQueries({
          queryKey: AuthInfiniteQueryKey["get:/wello/v2/auth/point-market/order"]()
        });
      }
    };
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const reissueCouponMutation = useMutation(t4);
  const copy = useClipboard();
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PageNavigation title={"\uAD6C\uB9E4\uB0B4\uC5ED"} hideHomeButton={true} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data_0 || $[10] !== hasNextPage || $[11] !== isLoading || $[12] !== ref || $[13] !== reissueCouponMutation) {
    t6 = !isLoading ? data_0?.contents.length ? <ul className="flex w-full flex-col gap-32 py-12">{data_0.contents.map(content => {
        const orderStatusText = ORDER_STATUS[content.orderStatus];
        const isUnused = orderStatusText === "\uC0AC\uC6A9\uC804";
        return <li key={content.orderIdIdx} className="px-16"><section className="flex items-center gap-6 rounded-xxsmall bg-Gray-50 p-12 font-600 text-Gray-600 typo-caption1"><strong className="font-700 text-Gray-black">{content.dday}</strong><hr className="h-14 w-1 border-none bg-Gray-400" />{dayjs(content.orderedAt).format("YYYY.MM.DD HH:mm")} 구매<strong className={cn("ml-auto font-700", isUnused ? "text-B-500" : "text-Gray-800")}>{orderStatusText}</strong></section><CustomLink className="mb-24 mt-16 block" href={ROUTES.POINT_MARKET_DETAIL.pathname({
            idx: content.productIdIdx
          })}><PointMarketListItem price={content.paidPrice} thumbnailUrl={content.thumbnailUrl} productName={content.productName} /></CustomLink><footer className="flex gap-5"><Button asChild={true} className="flex-1" dimensions="H40" theme="sub-line"><CustomLink href={ROUTES.POINT_MARKET_DETAIL.pathname({
                idx: content.productIdIdx
              })}>재구매</CustomLink></Button>{isUnused ? <Button className="flex-1" dimensions="H40" loading={reissueCouponMutation.isPending} onClick={() => {
              const {
                couponCode
              } = content;
              if (couponCode) {
                return setSelectedCouponCode(couponCode);
              }
              reissueCouponMutation.mutate({
                body: {
                  orderIdIdx: content.orderIdIdx
                }
              });
            }}>사용하기</Button> : null}</footer></li>;
      })}{hasNextPage ? <li className="flex-center" ref={ref}><Spinner /></li> : null}</ul> : <p className="m-auto text-center font-400 text-Gray-900 typo-body3">구매 내역이 없습니다.</p> : <div className="size-full flex-center"><Spinner /></div>;
    $[9] = data_0;
    $[10] = hasNextPage;
    $[11] = isLoading;
    $[12] = ref;
    $[13] = reissueCouponMutation;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = !!selectedCouponCode;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h3 className="mb-4 font-700 text-Gray-900 typo-body1">상품권 안내</h3>;
    t9 = <p className="text-Gray-600 typo-body4">모바일 상품권이 등록하신 휴대전화번호로 발송되었어요.</p>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ul className="ml-4 mt-12 font-400 text-Gray-900 typo-caption1"><li><span className="mr-4 font-800">·</span>문자를 받지 못하신 경우 아래 핀번호를 복사해 사용해주세요.</li><li><span className="mr-4 font-800">·</span>핀번호는 구매내역에서 언제든지 확인 가능해요.</li></ul>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== selectedCouponCode) {
    t11 = <Input value={selectedCouponCode} dimensions="H48" className="mb-12 mt-16" />;
    $[18] = selectedCouponCode;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = !selectedCouponCode;
  let t13;
  if ($[20] !== copy || $[21] !== selectedCouponCode) {
    t13 = () => {
      copy({
        message: "PIN \uBC88\uD638\uB97C \uBCF5\uC0AC\uD588\uC5B4\uC694",
        value: selectedCouponCode
      });
    };
    $[20] = copy;
    $[21] = selectedCouponCode;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13) {
    t14 = <Button dimensions="H56" className="my-12 w-full" disabled={t12} onClick={t13}>복사하기</Button>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t14 || $[28] !== t7) {
    t15 = <ResponsiveDialog opened={t7} onClose={closeBottomDrawer} drawerType="bottom">{t8}{t9}{t10}{t11}{t14}</ResponsiveDialog>;
    $[26] = t11;
    $[27] = t14;
    $[28] = t7;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t6) {
    t16 = <article className="mx-auto flex w-full max-w-screen-lg flex-col">{t5}{t6}{t15}</article>;
    $[30] = t15;
    $[31] = t6;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}
function _temp2(data) {
  if (!data) {
    return;
  }
  const contents = data.pages.flatMap(_temp);
  const lastPage = data.pages[data.pages.length - 1];
  return {
    totalCount: lastPage.context?.totalCount ?? 0,
    contents
  };
}
function _temp(page) {
  return page.context?.contents ?? [];
}